import { useEffect, useState } from "react";
import { MdBalance, MdForest, MdGroups, MdVerified } from "react-icons/md";
import { getInlineLoader } from "../Helpers";
import {
  aboutGalleryImages,
  company,
  coreValues,
  guestExperiencePillars,
  operationalStrengths,
  sustainabilityItems,
  whyChooseUs,
} from "../siteContent";
import OneObjective from "./OneObjective";

export default function AboutUsContent(props) {
  const [ready, setReady] = useState(false);

  async function init() {
    setReady(false);
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  if (!ready) {
    return (
      <div className="container mSupportLoading">
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <div className="MainBodySection" style={{ paddingBottom: "70px", paddingTop: "55px" }}>
      <div className="container">
        <div className="row g-4 align-items-center">
          <div className="col-lg-6">
            <div className="tff-section-kicker">About Us</div>
            <h2 className="tff-section-title">
              Zanzibar-based travel specialists with a strong mainland reach.
              <span className="tff-section-underline"></span>
            </h2>
            <p className="font-light tff-section-lead">
              {company.shortName} is a fully registered and professionally managed tour operating company serving
              Zanzibar and the United Republic of Tanzania. We are committed to providing high-quality, personalized
              travel services through accommodation planning, transport logistics, and excursion design.
            </p>
            <p className="font-light tff-section-lead">
              Our work is built around service excellence, trusted local coordination, and memorable guest experiences
              for leisure travelers, corporate groups, and special-interest visitors.
            </p>
          </div>

          <div className="col-lg-6">
            <div className="tff-about-visual mb-4">
              <div className="tff-about-visual-main">
                <img src={aboutGalleryImages[0]} alt="Zanzibar tourism experience" className="tff-about-visual-image" />
              </div>
              <div className="tff-about-visual-side">
                <img src={aboutGalleryImages[1]} alt="Zanzibar transport and tours" className="tff-about-visual-image" />
              </div>
              <div className="tff-about-floating-note">
                <p className="font-semi-bold mNoMargin">Island to mainland planning</p>
                <span className="font-light">Curated experiences with reliable local coordination</span>
              </div>
            </div>

            <div className="card tff-about-brand-card">
              <div className="card-body p-4 p-lg-5">
                <img src={company.logo} alt={company.shortName} className="tff-about-logo rounded-pill" />
                <div className="row g-3 mt-2">
                  <div className="col-sm-6">
                    <div className="tff-mini-stat">
                      <p className="font-light mb-1">Vision</p>
                      <h6 className="font-semi-bold mNoMargin">
                        To be a leading and trusted DMC in Zanzibar and Tanzania.
                      </h6>
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="tff-mini-stat">
                      <p className="font-light mb-1">Mission</p>
                      <h6 className="font-semi-bold mNoMargin">
                        To exceed expectations through reliable, personalized travel service.
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mt-4">
          <div className="col-lg-6">
            <div className="card h-100 tff-info-panel bg-primary">
              <div className="card-body p-4 p-lg-5">
                <div className="d-flex align-items-center mb-3">
                  <MdVerified size={28} className="me-2" />
                  <h3 className="font-bold mNoMargin text-light">Vision</h3>
                </div>
                <p className="font-light text-light mNoMargin">
                  To become a leading and most trusted destination management company in Zanzibar and Tanzania,
                  recognized for service excellence, innovation, and sustainable tourism practices.
                </p>
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card h-100 tff-info-panel">
              <div className="card-body p-4 p-lg-5">
                <div className="d-flex align-items-center mb-3">
                  <MdBalance size={28} className="me-2 text-primary" />
                  <h3 className="font-bold mNoMargin text-primary">Mission</h3>
                </div>
                <p className="font-light mNoMargin">
                  To provide exceptional, reliable, and personalized travel services that exceed customer expectations
                  while promoting Tanzania's cultural heritage, natural beauty, and environmental sustainability.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5">
          <div className="tff-section-kicker">Core Values</div>
          <h3 className="font-light text-primary mt-3 mb-4">The principles behind every guest experience</h3>
          <div className="row">
            {coreValues.map((item) => (
              <div key={item} className="col-md-4 mt-3">
                <OneObjective icon={<MdGroups size={36} className="text-secondary" />} content={item} />
              </div>
            ))}
          </div>
        </div>

        <div className="row g-4 mt-4">
          <div className="col-lg-6">
            <div className="card h-100 service-list-card overflow-hidden">
              <div className="row g-0 h-100">
                <div className="col-md-5">
                  <img
                    src={aboutGalleryImages[2]}
                    alt="Professional travel planning in Zanzibar and mainland Tanzania"
                    className="w-100 h-100"
                    style={{ objectFit: "cover", minHeight: "100%" }}
                  />
                </div>
                <div className="col-md-7">
                  <div className="card-body p-4 p-lg-5">
                    <div className="tff-section-kicker">What Sets Us Apart</div>
                    <h3 className="font-light text-primary mt-3 mb-3">
                      A capable team trusted to deliver smooth and satisfying journeys.
                    </h3>
                    <p className="font-light mb-3">
                      We combine destination knowledge, fast coordination, and practical problem-solving to make travel
                      feel easy for our guests. Whether the plan is a short island stay or a multi-stop Tanzania
                      itinerary, we focus on comfort, clarity, and dependable execution.
                    </p>
                    <p className="font-light mNoMargin">
                      Our guests benefit from responsive communication, carefully selected partners, and a service style
                      that is built around real attention to their preferences and expectations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card h-100 service-list-card service-list-card-alt">
              <div className="card-body p-4 p-lg-5">
                <div className="tff-section-kicker">Guest Confidence</div>
                <h3 className="font-light text-primary mt-3 mb-4">How we create experiences guests genuinely enjoy</h3>
                {guestExperiencePillars.map((item) => (
                  <div key={item.title} className="mb-4">
                    <div className="d-flex align-items-center mb-2">
                      <MdVerified size={20} className="text-secondary me-2 flex-shrink-0" />
                      <h6 className="font-semi-bold mNoMargin">{item.title}</h6>
                    </div>
                    <p className="font-light mNoMargin">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mt-1">
          <div className="col-lg-6">
            <div className="card h-100 service-list-card">
              <div className="card-body p-4 p-lg-5">
                <div className="tff-section-kicker">Operational Strengths</div>
                <h3 className="font-light text-primary mt-3 mb-4">Built for dependable travel delivery</h3>
                {operationalStrengths.map((item) => (
                  <div key={item} className="tff-inline-list-item">
                    <MdVerified size={20} className="text-secondary me-2 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card h-100 service-list-card service-list-card-alt">
              <div className="card-body p-4 p-lg-5">
                <div className="tff-section-kicker">Sustainability</div>
                <h3 className="font-light text-primary mt-3 mb-4">Responsible tourism that respects place and people</h3>
                {sustainabilityItems.map((item) => (
                  <div key={item} className="tff-inline-list-item">
                    <MdForest size={20} className="text-primary me-2 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mt-1">
          <div className="col-lg-4">
            <div className="card h-100 tff-about-brand-card overflow-hidden">
              <img
                src={aboutGalleryImages[3]}
                alt="Wildlife and safari experiences in Tanzania"
                className="w-100"
                style={{ height: "260px", objectFit: "cover" }}
              />
              <div className="card-body p-4">
                <div className="tff-section-kicker">Trusted Planning</div>
                <h4 className="font-light text-primary mt-3 mb-3">Personalized recommendations</h4>
                <p className="font-light mNoMargin">
                  We guide guests toward the right excursions, stays, and routes based on their interests, travel
                  style, and budget.
                </p>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="card h-100">
              <div className="card-body p-4 p-lg-5">
                <div className="tff-section-kicker">Our Promise</div>
                <h3 className="font-light text-primary mt-3 mb-3">
                  We are prepared, experienced, and deeply committed to guest satisfaction.
                </h3>
                <p className="font-light">
                  Our approach is simple: understand the guest, plan thoroughly, communicate clearly, and deliver with
                  care. That is how we create holidays, excursions, and safari experiences that feel polished from the
                  first inquiry to the final farewell.
                </p>
                <p className="font-light mNoMargin">
                  By pairing warm hospitality with professional coordination, {company.shortName} continues to position
                  itself as a dependable partner for travelers who want quality service in Zanzibar and mainland
                  Tanzania.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card mt-4">
          <div className="card-body p-4 p-lg-5">
            <div className="tff-section-kicker">Why Guests Return</div>
            <div className="row g-3 mt-2">
              {whyChooseUs.map((item) => (
                <div key={item} className="col-md-6">
                  <div className="tff-inline-list-item">
                    <MdVerified size={20} className="text-secondary me-2 flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
